import type { BlockKind, VoxelWorld } from "../../types";
import { placeBlock } from "./placement";
import type { StructureSpot } from "./types";

type CellPredicate = (x: number, y: number, z: number) => boolean;

export class StructureBrush {
    private readonly baseY: number;

    constructor(
        private readonly world: VoxelWorld,
        private readonly spot: StructureSpot,
        groundTop: number = spot.topAbs,
    ) {
        this.baseY = groundTop + 1;
    }

    block(x: number, y: number, z: number, kind: BlockKind): void {
        placeBlock(
            this.world,
            this.spot.x + x,
            this.spot.z + z,
            this.baseY + y,
            kind,
        );
    }

    column(
        x: number,
        z: number,
        fromY: number,
        toY: number,
        kind: BlockKind,
    ): void {
        for (let y = fromY; y <= toY; y++) this.block(x, y, z, kind);
    }

    lineX(fromX: number, toX: number, y: number, z: number, kind: BlockKind): void {
        for (let x = fromX; x <= toX; x++) this.block(x, y, z, kind);
    }

    lineZ(x: number, y: number, fromZ: number, toZ: number, kind: BlockKind): void {
        for (let z = fromZ; z <= toZ; z++) this.block(x, y, z, kind);
    }

    layer(
        fromX: number,
        toX: number,
        fromZ: number,
        toZ: number,
        y: number,
        kind: BlockKind,
        includes: CellPredicate = () => true,
    ): void {
        for (let x = fromX; x <= toX; x++) {
            for (let z = fromZ; z <= toZ; z++) {
                if (includes(x, y, z)) this.block(x, y, z, kind);
            }
        }
    }

    box(
        fromX: number,
        toX: number,
        fromY: number,
        toY: number,
        fromZ: number,
        toZ: number,
        kind: BlockKind,
        includes: CellPredicate = () => true,
    ): void {
        for (let y = fromY; y <= toY; y++) {
            this.layer(fromX, toX, fromZ, toZ, y, kind, includes);
        }
    }

    perimeter(
        fromX: number,
        toX: number,
        fromY: number,
        toY: number,
        fromZ: number,
        toZ: number,
        kind: BlockKind,
        includes: CellPredicate = () => true,
    ): void {
        this.box(fromX, toX, fromY, toY, fromZ, toZ, kind, (x, y, z) => {
            const onEdge = x === fromX || x === toX || z === fromZ || z === toZ;
            return onEdge && includes(x, y, z);
        });
    }
}
